import { auth } from '../firebase'; // Import auth for user authentication
import { terminology } from '../config/terminology'; // Import terminology for dynamic labels

// Define API_BASE_URL (ensure this matches your environment setup)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3800';

/**
 * Fetches the distinct households serviced by a franchisee, used to preload the
 * household filter dropdown. Aggregated server-side from the franchisee's schedules.
 *
 * @param {string} fseId - The franchisee ID.
 * @returns {Promise<Array<{householdId: string, householdInfo: object}>>}
 * @throws {Error} - If the request fails.
 */
export const getFranchiseeHouseholds = async (fseId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated.');
  }
  if (!fseId) {
    return [];
  }

  const idToken = await user.getIdToken();

  const response = await fetch(`${API_BASE_URL}/job/franchisee-households`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fseId }),
  });

  const result = await response.json();

  if (response.ok && result.status === 'success') {
    return result.data?.households || [];
  }
  throw new Error(result.message || 'Failed to load households.');
};

/**
 * Fetches details for a specific household (client in terminology) by ID.
 * @param {string} householdId - The ID of the household to fetch.
 * @returns {Promise<object>} - The household data.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getHouseholdById = async (householdId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!householdId) {
    throw new Error(`${terminology.household.singular} ID is required.`);
  }

  try {
    const idToken = await user.getIdToken();
    // Assuming the endpoint requires POST and householdId in the body
    const url = `${API_BASE_URL}/household/get-by-id`; // Adjust endpoint if needed
    const payload = { householdId };

    console.log("API Request POST to /household/get-by-id with payload:", JSON.stringify(payload));


    const response = await fetch(url, {
      method: 'POST', // Or GET if the API uses /household/:id
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json', // Needed for POST with body
      },
      body: JSON.stringify(payload), // Send ID in body for POST
    });

    const responseData = await response.json();
    console.log("API Response from /household/get-by-id:", responseData);


    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || `Failed to fetch ${terminology.household.singular.toLowerCase()} details`);
    }

    // Assuming data is nested under 'data' key in the response
    return responseData.data;

  } catch (error) {
    console.error(`Error fetching ${terminology.household.singular.toLowerCase()} ${householdId}:`, error);
    throw error; // Re-throw the error for the calling component to handle
  }
};
